import * as app from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import 'firebase/storage';
import 'firebase/functions';
import * as Cookies from 'js-cookie';

import routes, {baseUrl} from '../../constants/routes';

import {ICourseFeedback} from "../../model/CourseFeedback";
import {FileType, ICourseMaterial} from "../../model/CourseMaterial";
import {IProfessor} from "../../model/Professor";
import Constants from '../../constants';

// init firebase
const prodConfig = {
  apiKey: "AIzaSyBZtHMEJahXihu1Jhdw-lXx8hzyxbmn6pw",
  authDomain: "nurate-b4ed4.firebaseapp.com",
  databaseURL: "https://nurate-b4ed4.firebaseio.com",
  projectId: "nurate-b4ed4",
  storageBucket: "nurate-b4ed4.appspot.com",
  messagingSenderId: "15376762174"
};

const devConfig = {
  apiKey: "AIzaSyAzTLfVRVF5jsUXWy9PDOxdSRCfxpyJowg",
  authDomain: "nurate-dev-4f889.firebaseapp.com",
  databaseURL: "https://nurate-dev-4f889.firebaseio.com",
  projectId: "nurate-dev-4f889",
  storageBucket: "nurate-dev-4f889.appspot.com",
  messagingSenderId: "1081788338563"
}

export const firebaseConfig = prodConfig;

class Firebase {

  auth: app.auth.Auth;
  firestore: app.firestore.Firestore;
  storage: app.storage.Storage;
  
  serverTimestamp: () => app.firestore.FieldValue;
  functions: app.functions.Functions;

   constructor() {
     app.initializeApp(firebaseConfig);

     this.auth = app.auth();
     this.firestore = app.firestore();
     this.firestore.settings({timestampsInSnapshots: true});
     this.storage = app.storage();
     this.functions = app.functions();

     this.serverTimestamp = app.firestore.FieldValue.serverTimestamp;
  }

  get university() {
     return this.firestore.collection('universities').doc('nu.edu.kz');
  }

  get users() {
     return this.university.collection('users');
  }

  // AUTH
  sendSignInLink = async (email: string) => {
    const verifyEmail = this.functions.httpsCallable('verifyEmail');
    const result = await verifyEmail({email});
    console.log(result.data);
    if (result.data === 'Valid email') {
      const actionCodeSettings = {
        // URL you want to redirect back to. The domain (www.example.com) for this
        // URL must be whitelisted in the Firebase Console.
        url: `${baseUrl}${routes.SIGN_IN_VERIFICATION}`,
        handleCodeInApp: true,
      };
      window.localStorage.setItem('emailForSignIn', email);
      return this.auth.sendSignInLinkToEmail(email, actionCodeSettings);
    } else {
      return Promise.reject('Invalid email');
    }

  };

  verifySignIn = async () => {
    return new Promise(async (resolve, reject) => {

      if (!this.auth.isSignInWithEmailLink(window.location.href)) {
        reject(new Error('not link'));
      }

      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        email = window.prompt('Please provide your email for confirmation');
      }
      if (email == null) {
        email = '';
        reject(new Error('not valid email'));
      }

      this.auth.setPersistence(app.auth.Auth.Persistence.LOCAL).catch((err) => {
        reject(err);
      });

      this.auth.signInWithEmailLink(email, window.location.href)
        .then((result: any) => {
          window.localStorage.removeItem('emailForSignIn');
          resolve(result);
        }).catch((err: any) => {
          reject(err);
        });
    });

  };

  signOut = () => {
    return this.auth.signOut();
  };

  // API
  getUniversity = () => {
    return this.university.get();
  };

  searchCoursesByName = (name: string) => {
    const coursesRef = this.university.collection('courses');
    let query = coursesRef.limit(100);

    // remove non alphanumeric characters from search string
    const tokens = name.trim().replace(/[^\w\s]/gi, '')
      .split(' ').filter(t => t > '');

    for (const token of tokens) {
      query = query.where(`searchTokens.${token}`, '==', true);
    }
    return query.get();
  }

  getCourse = (courseSlug: string) => {
    return this.university.collection('courses').doc(courseSlug).get();
  };

  getCourseFeedbacks = async (courseSlug: string) => {
    try {
      const querySnapshot = await this.university
        .collection('courses').doc(courseSlug)
        .collection('feedbacks')
        .orderBy('createdAt', 'desc')
        .get();
      const feedbacks: ICourseFeedback[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as ICourseFeedback;
        feedbacks.push(data);
      });
      return Promise.resolve(feedbacks);

    } catch (err) {
      return Promise.reject(err);
    }
  };

  createFeedback = (courseFeedback: ICourseFeedback, courseSlug: string) => {
    return this.university.collection('courses').doc(courseSlug)
      .collection('feedbacks').add({
        ...courseFeedback,
        // TODO: hack, since cloud function will not have access to auth user, firebase team is working on it
        userId: Cookies.get(Constants.uid)
      });
  };

  getMaterials = async (fileType: FileType, courseSlug: string) => {
    try {
      const querySnapshot = await this.university.collection('courses').doc(courseSlug)
        .collection('materials')
        .where('fileType', '==', fileType)
        // .orderBy('createdAt', 'desc')
        .get();
      const materials: ICourseMaterial[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as ICourseMaterial;
        materials.push(data);
      });
      console.log(materials);
      return Promise.resolve(materials);

    } catch (err) {
      return Promise.reject(err);
    }

  };

  createMaterial = async (courseMaterial: ICourseMaterial, file: File, courseSlug: string) => {
    // upload file first
    try {
      const fileName = `${courseMaterial.fileName}-${Date.now()}`;
      const snapshot = await this.storage.ref().child(`nu.edu.kz/${courseSlug}/${fileName}`).put(file);
      courseMaterial.downloadUrl = await snapshot.ref.getDownloadURL();

      return this.university.collection('courses').doc(courseSlug)
        .collection('materials')
        .add({
          ...courseMaterial,
          userId: Cookies.get(Constants.uid)
        })

    } catch (err) {
      console.log(err);
      return Promise.reject(err);
    }

  };

  // deprecated
  getAllProfessors = async () => {
    try {
      const querySnapshot = await this.university.collection('professors').get();
      const professors: IProfessor[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        professors.push({
          name: data.name,
          slug: data.slug
        });
      });
      return Promise.resolve(professors);

    } catch (err) {
      return Promise.reject(err);
    }

  }

  getUser = async (uid: string) => {
    return this.university.collection('users').doc(uid).get();
  }

  addCourseToUser = (courseSlug: string, remove: boolean) => {
    this.auth.onAuthStateChanged(async user => {
      if (user) {
        await this.university.collection('users').doc(user.uid).update({
          courses: remove ? app.firestore.FieldValue.arrayRemove(courseSlug) : app.firestore.FieldValue.arrayUnion(courseSlug)
        });
        return Promise.resolve();
      }
      return Promise.reject('No user signed in');
    })
  }

  createNurateFeedback = (message: string) => {
    return this.university.collection('nurateFeedbacks').add({
      message,
      userId: Cookies.get(Constants.uid) || '',
      username: Cookies.get(Constants.username) || '',
    });
  }

}


export default Firebase;